import { useEffect, useRef } from 'react';
import styles from './OrbitCanvas.module.css';

export default function OrbitCanvas({ 
  planetA, 
  planetB, 
  compatibility,
  archetypes 
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const planetARef = useRef(null);
  const planetBRef = useRef(null);

  const archA = archetypes[planetA];
  const archB = archetypes[planetB];

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    const planetAEl = planetARef.current;
    const planetBEl = planetBRef.current;
    if (!container || !canvas || !planetAEl || !planetBEl) return;
    
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    const rect = container.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    ctx.scale(dpr, dpr);
    
    const cx = w / 2;
    const cy = h / 2;
    
    // 根据屏幕宽度调整星球大小
    const isMobile = w < 500;
    const planetSize = isMobile ? 50 : 80;
    const planetOffset = planetSize / 2;
    
    // 轨道参数 - 根据契合度和屏幕大小调整
    const baseRx = Math.min(w, h) * (isMobile ? 0.42 : 0.3);
    const baseRy = baseRx * 0.5;
    const gap = (100 - compatibility) * 0.25 + 25;
    
    const orbitA = { rx: baseRx, ry: baseRy };
    const orbitB = { rx: baseRx + gap, ry: baseRy + gap * 0.5 };
    
    let time = 0;
    const speedA = 0.004;
    const speedB = 0.0025;
    
    function draw() {
      ctx.clearRect(0, 0, w, h);
      
      // 绘制轨道
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 1;
      
      // 内轨道
      ctx.beginPath();
      ctx.ellipse(cx, cy, orbitA.rx, orbitA.ry, 0, 0, Math.PI * 2);
      ctx.stroke();
      
      // 外轨道
      ctx.beginPath();
      ctx.ellipse(cx, cy, orbitB.rx, orbitB.ry, 0, 0, Math.PI * 2);
      ctx.stroke();
      
      // 中心光点
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 12);
      grad.addColorStop(0, 'rgba(255, 255, 255, 0.5)');
      grad.addColorStop(0.5, 'rgba(255, 255, 255, 0.15)');
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, 12, 0, Math.PI * 2);
      ctx.fill();
      
      // 计算星球位置
      const angleA = time * speedA;
      const angleB = time * speedB + Math.PI;
      
      const posA = {
        x: cx + Math.cos(angleA) * orbitA.rx,
        y: cy + Math.sin(angleA) * orbitA.ry,
      };
      const posB = {
        x: cx + Math.cos(angleB) * orbitB.rx,
        y: cy + Math.sin(angleB) * orbitB.ry,
      };
      
      // 远近效果
      const scaleA = 0.85 + (Math.sin(angleA) + 1) * 0.15;
      const scaleB = 0.85 + (Math.sin(angleB) + 1) * 0.15;
      
      // 更新星球位置 - 使用动态偏移量
      planetAEl.style.transform = `translate(${posA.x - planetOffset}px, ${posA.y - planetOffset}px) scale(${scaleA})`;
      planetAEl.style.zIndex = Math.sin(angleA) > 0 ? 10 : 1;
      
      planetBEl.style.transform = `translate(${posB.x - planetOffset}px, ${posB.y - planetOffset}px) scale(${scaleB})`;
      planetBEl.style.zIndex = Math.sin(angleB) > 0 ? 10 : 1;
      
      time++;
      animRef.current = requestAnimationFrame(draw);
    }
    
    draw();
    
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [compatibility, archetypes, planetA, planetB]);
  
  return (
    <div ref={containerRef} className={styles.wrapper}>
      <canvas ref={canvasRef} className={styles.canvas} />
      
      {archA && (
        <div ref={planetARef} className={styles.planet}>
          <img src={archA.planetImg} alt={archA.planet} />
        </div>
      )}
      
      {archB && (
        <div ref={planetBRef} className={styles.planet}>
          <img src={archB.planetImg} alt={archB.planet} />
        </div>
      )}
    </div>
  );
}
